import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "react-toastify";
import {
    getUserData,
    setUserCookies,
    getTransactions,
    updateCustomerApi,
    updatePasswordApi,
    updateUserData,
} from "../User/action.js";
import { PUT_CREATE_BOX } from "../boxOrder/action";
import { postCreateReturnOrder } from "../boxOrder/action";
import { forgotPasswordApi } from "../User/action";
import {
    ACCESS_TOKEN,
    REWORK,
    SUBMITTED_SURVEY,
    PAYMENT_STATUS,
    EMAIL_STATUS,
    RESET_PASSWORD_SUCCESS_MSG,
} from "../../public/Constants/EnumConstants";
import { deleteCookie } from "cookies-next";
import useBoxOrderStore from "./useBoxOrderStore";

const useUserStore = create(
    persist(
        (set, get) => ({
            userData: null,
            accessToken: null,
            nextStep: null,
            previousStep: null,
            loading: false,
            boxItems: [],
            returnStep: 1,
            instructionsPage: "",
            error: null,
            forgotPasswordSuccess: null,
            wantDiscount: false,
            personality: null,
            hasUserHydrated: false,

            // Actions
            resetUser: () => {
                deleteCookie(ACCESS_TOKEN, null);
                deleteCookie(SUBMITTED_SURVEY, null);
                deleteCookie(PAYMENT_STATUS, null);
                deleteCookie(EMAIL_STATUS, null);
                set({
                    userData: null,
                    accessToken: null,
                    nextStep: null,
                    previousStep: null,
                    loading: false,
                    boxItems: [],
                    returnStep: 1,
                    instructionsPage: "",
                    error: null,
                    forgotPasswordSuccess: null,
                });
            },

            updateUserData: (data) => {
                const userData = get()?.userData;
                set({ userData: { ...userData, ...data } });
            },

            resetState: (type) => {
                if (type === "error") set({ error: null });
                else if (type === "forgotPasswordSuccess")
                    set({ forgotPasswordSuccess: false });
            },

            changeDiscountFlag: (flag) => {
                set({ wantDiscount: flag });
            },

            setPersonality: (personality) => {
                set({ personality });
            },

            addItem: (item) => {
                const boxItems = get().boxItems;
                const existingItemIndex = boxItems.findIndex(
                    (i) => i.id === item.id
                );
                if (existingItemIndex !== -1) {
                    boxItems[existingItemIndex] = item;
                } else {
                    boxItems.push(item);
                }
                set({ boxItems });
            },

            setEmailVerified: () => {
                const user = get().userData;
                const updatedUser = { ...user, email_verified: true };
                set({ userData: updatedUser });
                setUserCookies(updatedUser);
            },

            changeStep: (step) => set({ returnStep: step }),

            changeInstructionsPage: (page) => {
                set({ instructionsPage: page, returnStep: 3 });
            },

            resetBoxItems: () => {
                set({ boxItems: [], returnStep: 1 });
            },

            // Async actions

            getCustomerData: async (payload) => {
                set({ loading: true, error: null });
                try {
                    const response = await getUserData();
                    await setUserCookies(response);
                    if (response?.user) {
                        set({ userData: response, loading: false });
                    } else {
                        set({ loading: false });
                    }
                    return response;
                } catch (error) {
                    set({ loading: false, error });
                }
            },

            updateCustomer: async (payload, showToast = true) => {
                set({ loading: true, error: null });

                try {
                    const response = await updateCustomerApi(payload, true);
                    if (response?.user && showToast) {
                        toast("Profile updated successfully", {
                            type: "success",
                        });
                        await set({ userData: response, loading: false });
                    } else {
                        showToast && toast("There was a problem updating your profile", {
                            type: "error",
                        });

                        set({ loading: false });
                    }
                    return response;
                } catch (error) {
                    showToast && toast("There was a problem updating your profile", {
                        type: "error",
                    });
                    set({ loading: false, error });
                }
            },

            updateCustomerData: async (payload, isUpdate) => {
                set({ loading: true, error: null, success: null });
                try {
                    const response = await updateUserData(payload, isUpdate);
                    if (response?.user) {
                        toast("Profile updated successfully", {
                            type: "success",
                        });
                        set({ userData: response, loading: false });
                    } else {
                        toast("There was a problem updating your profile", {
                            type: "error",
                        });
                        set({ loading: false });
                    }
                    return data;
                } catch (error) {
                    set({ loading: false, error });
                }
            },

            updatePassword: async (payload) => {
                const { old_password, new_password, reset } = payload;
                const passwordPayload = { old_password, new_password };
                set({ loading: true, error: null });
                try {
                    const response = await updatePasswordApi(passwordPayload);
                    if (response?.token) {
                        toast("Password updated successfully", {
                            type: "success",
                        });
                        reset();
                        set({
                            userData: {
                                ...get().userData,
                                last_password_update: response.date_created,
                            },
                            loading: false,
                        });
                    } else {
                        toast(
                            response?.error ||
                            response?.new_password?.[0] ||
                            "There was a problem updating your password",
                            { type: "error" }
                        );
                        set({ loading: false });
                    }
                } catch (error) {
                    set({ loading: false, error });
                }
            },

            keepAllBox: async (payload) => {
                const getUserBoxes = useBoxOrderStore?.getState()?.getUserBoxes;
                set({ loading: true });
                try {
                    const response = await PUT_CREATE_BOX(payload);
                    if (response?.customer) {
                        await getUserBoxes();
                    } else {
                        set({ loading: false });
                    }
                } catch {
                    set({ loading: false });
                }
            },

            returnOrder: async (payload) => {
                const getUserBoxes = useBoxOrderStore?.getState()?.getUserBoxes;
                set({ loading: true });
                try {
                    const response = await postCreateReturnOrder(payload);
                    if (response?.customer) {
                        await getUserBoxes();
                        const instructionsPage =
                            payload.full_return && payload.next_steps?.length
                                ? payload.next_steps[0].step
                                : REWORK;
                        set({
                            loading: false,
                            instructionsPage,
                            returnStep: 3,
                        });
                    } else {
                        set({ loading: false });
                    }
                } catch {
                    set({ loading: false });
                }
            },

            forgotPassword: async (email) => {
                set({
                    loading: true,
                    error: null,
                    forgotPasswordSuccess: null,
                });
                try {
                    const response = await forgotPasswordApi(email);
                    if (response?.message === RESET_PASSWORD_SUCCESS_MSG) {
                        set({
                            loading: false,
                            forgotPasswordSuccess: response?.message,
                        });

                        window.location.href = "/login?toast=true";

                        return response?.message;
                    } else {
                        throw new Error(response?.message);
                    }
                } catch (error) {
                    set({
                        loading: false,
                        error: error?.message,
                        forgotPasswordSuccess: null,
                    });
                    console.error({ error });
                    toast(error?.message, { type: "error" });
                }
            },

            setHasHydrated: (state) => {
                set({
                    hasUserHydrated: state,
                });
            },
        }),
        {
            name: "user-store",
            onRehydrateStorage: (state) => {
                return () => state.setHasHydrated(true);
            },
        }
    )
);

export default useUserStore;
